export interface QualityConfig {
  ignoreZeroValues: boolean;
  removeOutliers: boolean;
  minResponseThreshold: number;
  confidenceLevel: number;
  handleMissingData: 'ignore' | 'interpolate' | 'exclude';
  outlierMethod: 'iqr' | 'zscore' | 'both';
}

export interface QualityReport {
  totalResponses: number;
  validResponses: number;
  missingData: number;
  zeroValues: number;
  outliers: number[];
  qualityScore: number;
  recommendations: string[];
}

export class DataQualityAnalyzer {
  private config: QualityConfig;

  constructor(data: any[], sampleSize?: number) {
    this.config = this.determineOptimalConfig(data, sampleSize);
  }

  private determineOptimalConfig(data: any[], sampleSize?: number): QualityConfig {
    const totalResponses = sampleSize || data.length;
    const zeroCount = this.countZeroValues(data);
    const missingCount = this.countMissingValues(data);
    
    // نسبة القيم الصفرية
    const zeroRatio = zeroCount / totalResponses;
    // نسبة البيانات المفقودة
    const missingRatio = missingCount / totalResponses;
    
    return {
      // تجاهل القيم الصفرية إذا كانت نسبتها أكبر من 15%
      ignoreZeroValues: zeroRatio > 0.15,
      // إزالة القيم الشاذة إذا كان حجم العينة كبير (أكثر من 50)
      removeOutliers: totalResponses > 50,
      // الحد الأدنى للاستجابات المقبولة
      minResponseThreshold: Math.max(30, Math.floor(totalResponses * 0.7)),
      // مستوى الثقة (95% إذا كانت العينة كبيرة، 90% إذا كانت صغيرة)
      confidenceLevel: totalResponses > 100 ? 0.95 : 0.90,
      // طريقة التعامل مع البيانات المفقودة
      handleMissingData: missingRatio > 0.1 ? 'exclude' : 'ignore',
      // طريقة اكتشاف القيم الشاذة
      outlierMethod: totalResponses > 100 ? 'both' : 'iqr'
    };
  }

  private countZeroValues(data: any[]): number {
    let count = 0;
    data.forEach(row => {
      Object.values(row).forEach(value => {
        if (typeof value === 'number' && value === 0) count++;
        if (typeof value === 'string' && (value === '0' || value.trim() === '')) count++;
      });
    });
    return count;
  }

  private countMissingValues(data: any[]): number {
    let count = 0;
    data.forEach(row => {
      Object.values(row).forEach(value => {
        if (value === null || value === undefined || value === '') count++;
      });
    });
    return count;
  }

  private detectOutliers(values: number[], method: 'iqr' | 'zscore' | 'both'): number[] {
    const outliers: number[] = [];
    
    if (method === 'iqr' || method === 'both') {
      const sorted = [...values].sort((a, b) => a - b);
      const q1 = sorted[Math.floor(sorted.length * 0.25)];
      const q3 = sorted[Math.floor(sorted.length * 0.75)];
      const iqr = q3 - q1;
      const lowerBound = q1 - 1.5 * iqr;
      const upperBound = q3 + 1.5 * iqr;
      
      values.forEach((value, index) => {
        if (value < lowerBound || value > upperBound) {
          outliers.push(index);
        }
      });
    }
    
    if (method === 'zscore' || method === 'both') {
      const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
      const std = Math.sqrt(values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length);
      
      values.forEach((value, index) => {
        const zscore = Math.abs((value - mean) / std);
        if (zscore > 2.5 && !outliers.includes(index)) {
          outliers.push(index);
        }
      });
    }
    
    return outliers;
  }

  cleanData(data: any[]): { cleanedData: any[], report: QualityReport } {
    let cleanedData = [...data];
    const originalCount = data.length;
    let removedOutliers = 0;
    let removedZeros = 0;
    let removedMissing = 0;

    // إزالة أو معالجة القيم الصفرية
    if (this.config.ignoreZeroValues) {
      cleanedData = cleanedData.filter(row => {
        const hasSignificantData = Object.values(row).some(value => {
          if (typeof value === 'number') return value > 0;
          if (typeof value === 'string') return value.trim() !== '' && value !== '0';
          return true;
        });
        if (!hasSignificantData) removedZeros++;
        return hasSignificantData;
      });
    }

    // معالجة البيانات المفقودة
    if (this.config.handleMissingData === 'exclude') {
      cleanedData = cleanedData.filter(row => {
        const hasCompleteData = Object.values(row).every(value => 
          value !== null && value !== undefined && value !== ''
        );
        if (!hasCompleteData) removedMissing++;
        return hasCompleteData;
      });
    }

    // إزالة القيم الشاذة
    const outlierIndices: number[] = [];
    if (this.config.removeOutliers) {
      const numericalColumns = this.getNumericalColumns(cleanedData);
      
      numericalColumns.forEach(column => {
        const values = cleanedData.map(row => parseFloat(row[column])).filter(val => !isNaN(val));
        const columnOutliers = this.detectOutliers(values, this.config.outlierMethod);
        outlierIndices.push(...columnOutliers);
      });

      const uniqueOutliers = [...new Set(outlierIndices)];
      cleanedData = cleanedData.filter((_, index) => !uniqueOutliers.includes(index));
      removedOutliers = uniqueOutliers.length;
    }

    const report: QualityReport = {
      totalResponses: originalCount,
      validResponses: cleanedData.length,
      missingData: removedMissing,
      zeroValues: removedZeros,
      outliers: outlierIndices,
      qualityScore: this.calculateQualityScore(cleanedData.length, originalCount),
      recommendations: this.generateRecommendations(cleanedData.length, originalCount, removedZeros, removedMissing, removedOutliers)
    };

    return { cleanedData, report };
  }

  private getNumericalColumns(data: any[]): string[] {
    if (data.length === 0) return [];
    
    const firstRow = data[0];
    return Object.keys(firstRow).filter(key => {
      const value = firstRow[key];
      return typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)));
    });
  }

  private calculateQualityScore(validCount: number, totalCount: number): number {
    const completenessScore = (validCount / totalCount) * 100;
    return Math.round(completenessScore);
  }

  private generateRecommendations(validCount: number, totalCount: number, zeros: number, missing: number, outliers: number): string[] {
    const recommendations: string[] = [];
    
    if (validCount < totalCount * 0.7) {
      recommendations.push('⚠️ البيانات تحتاج إلى مراجعة - أقل من 70% من الاستجابات صالحة للتحليل');
    }
    
    if (zeros > totalCount * 0.1) {
      recommendations.push('🔍 يُنصح بمراجعة تصميم الاستبيان - نسبة عالية من القيم الصفرية');
    }
    
    if (missing > totalCount * 0.15) {
      recommendations.push('📝 يُنصح بجعل الأسئلة المهمة إجبارية - نسبة عالية من البيانات المفقودة');
    }
    
    if (outliers > totalCount * 0.05) {
      recommendations.push('📊 تم اكتشاف قيم شاذة - قد تحتاج لمراجعة يدوية');
    }
    
    if (validCount >= totalCount * 0.9) {
      recommendations.push('✅ جودة البيانات ممتازة - يمكن الاعتماد على النتائج بثقة عالية');
    }
    
    if (validCount < 30) {
      recommendations.push('📏 حجم العينة صغير - النتائج قد لا تكون ممثلة إحصائياً');
    }
    
    return recommendations;
  }

  getConfig(): QualityConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<QualityConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
} 